//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';

const DEFAULT_SIZE = 16;
const SM_SIZE = 12;
const LG_SIZE = 24;

interface Props {
  className?: string;
  name: UnisonIconType;
}

/**
 * The Icon atom renders an SVG icon that will inherit the current text color of the container
 * it is in. The icon defaults to a 16x16px size unless the icon name is suffixed with a size
 * indicator, in which case it will be 24x24px (-lg) or 12x12px (-sm).
 *
 * The Icon can be styled directly via styled-components.
 */
export const Icon = ({ className, name }: Props) => {
  const size = (() => {
    if (name.includes('-sm')) {
      return SM_SIZE;
    } else if (name.includes('-lg')) {
      return LG_SIZE;
    } else {
      return DEFAULT_SIZE;
    }
  })();

  // We must use require syntax for a dynamic import so disable eslint to avoid errors.
  // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
  const iconSrc: string = require(
    `@bvengineering/unison-core/lib/icons/${name}.svg`,
  ) as string;

  return (
    <SvgComponent
      aria-hidden={true}
      className={className}
      focusable={false}
      size={size}
      src={iconSrc}
    />
  );
};

const SvgComponent = styled(SVG)<{ size: number }>(
  ({ size }) => `
  width: ${size}px;
  height: ${size}px;
  /** Sets the icon to the same color as the current font color */
  fill: currentColor;
  /** Ensures that icons don't unintentionally resize inside of flex containers */
  flex: none;
  /* Required for MS Edge to prevent clicks on SVG elements */
  pointer-events: none;
`,
);

export default Icon;
